import { useMsal } from '@azure/msal-react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  BoatModelsList,
  BOAT_MODEL_LENGTH_RANGE_LABELS,
  getBoatLengthRangeDisplayLabel,
} from '../../helpers/BoatModelsListSetup';
import { User } from '../../models/ApiModels';
import { GetUser } from '../../services/UserService';
import { RootState } from '../../store/reducers/combine';
import useRequest from '../../utils/net/useRequest';

interface BoatModelsMiniDashboardProps {
  boatModels: BoatModelsList[];
  searchValue: string;
}

type LengthRangeSummary = {
  label: string;
  count: number;
  percentage: number;
};

function BoatModelsMiniDashboard({ boatModels, searchValue }: BoatModelsMiniDashboardProps) {
  const { accounts } = useMsal();
  const account = accounts[0];
  const totalBoatModels = boatModels.length;
  const systems = useSelector((state: RootState) => state.systems.systems);
  const [currentUser] = useRequest<User>(
    () => GetUser(account?.idTokenClaims?.extension_oemUserId as string),
    [account]
  );
  const userPrefersImperial = currentUser?.settings?.measurementSystem === 'Imperial';

  const { lengthRangeSummary, totalSystemsInSelection } = useMemo(() => {
    const lengthRangeByBoatModelId = new Map<string, string>();
    boatModels.forEach((boatModel) => {
      if (!boatModel.boatModelId) return;
      lengthRangeByBoatModelId.set(boatModel.boatModelId, boatModel.lengthRange);
    });

    const countByRange: Record<string, number> = {};
    BOAT_MODEL_LENGTH_RANGE_LABELS.forEach((rangeLabel) => {
      countByRange[rangeLabel] = 0;
    });

    let matchedSystems = 0;
    systems.forEach((system) => {
      if (!system.boatModelId) return;
      const lengthRange = lengthRangeByBoatModelId.get(system.boatModelId);
      if (!lengthRange) return;
      countByRange[lengthRange] += 1;
      matchedSystems += 1;
    });

    return {
      totalSystemsInSelection: matchedSystems,
      lengthRangeSummary: BOAT_MODEL_LENGTH_RANGE_LABELS.map((label): LengthRangeSummary => {
        const count = countByRange[label];
        const percentage = matchedSystems > 0 ? Math.round((count / matchedSystems) * 100) : 0;
        return { label, count, percentage };
      }),
    };
  }, [boatModels, systems]);

  return (
    <div className='border-x border-b border-gray-10 bg-white px-3 py-2'>
      <div className='flex flex-wrap items-center justify-between gap-2 mb-2'>
        <span className='prose-labelStandard text-gray-60'>Insights</span>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-4 gap-2'>
        <div className='bg-white border border-gray-10 px-3 py-2'>
          <div className='prose-paragraphSmaller text-gray-60'>Models</div>
          <div className='prose-heading4 text-gray-100'>{totalBoatModels}</div>
          <div className='prose-paragraphSmaller text-gray-60 mt-2'>Systems</div>
          <div className='prose-heading4 text-gray-100'>{totalSystemsInSelection}</div>
        </div>

        <div className='bg-white border border-gray-10 px-3 py-2 lg:col-span-3'>
          <div className='prose-labelTableColumn text-gray-60 mb-2'>Length spread (systems)</div>
          <div className='flex flex-col gap-2'>
            {lengthRangeSummary.map((item) => (
              <div key={item.label} className='grid grid-cols-[70px_minmax(0,1fr)_90px] gap-2 items-center'>
                <span className='prose-paragraphSmaller text-gray-80'>
                  {getBoatLengthRangeDisplayLabel(item.label, userPrefersImperial)}
                </span>
                <div className='h-1.5 bg-gray-10 rounded-full overflow-hidden'>
                  <div
                    className='h-full bg-primary-100 rounded-full'
                    style={{ width: item.count > 0 && item.percentage === 0 ? '2px' : `${item.percentage}%` }}
                  />
                </div>
                <span className='prose-paragraphSmaller text-gray-80 text-right'>
                  {item.count} ({item.percentage}%)
                </span>
              </div>
            ))}
          </div>
          {(totalBoatModels === 0 || totalSystemsInSelection === 0) && (
            <div className='prose-paragraphSmaller italic text-gray-60 mt-2'>
              {totalBoatModels === 0
                ? 'No boat models found for this filter.'
                : 'No systems assigned to boat models in this filter.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BoatModelsMiniDashboard;
